import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { CheckCircle2, AlertCircle, Activity } from "lucide-react";

const COMPONENTS = [
  { key: "marketplace", label: "Marketplace Web App", path: "/" },
  { key: "api", label: "Public REST API", path: "/health" },
  { key: "auth", label: "Authentication Service", path: "/auth/me" }, // returns 401 unauth — counted as "up"
  { key: "catalog", label: "Service Catalog", path: "/services" },
];

export default function Status() {
  const [statuses, setStatuses] = useState({});
  const [ts, setTs] = useState(null);

  useEffect(() => {
    const check = async () => {
      const out = {};
      await Promise.all(
        COMPONENTS.map(async (c) => {
          const start = performance.now();
          try {
            const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api${c.path}`, { credentials: "include" });
            // Treat any 2xx or 4xx as "up" (the server responded); 5xx or network error = "down"
            out[c.key] = { up: r.status < 500, latency: Math.round(performance.now() - start), code: r.status };
          } catch (e) {
            out[c.key] = { up: false, latency: 0, code: 0 };
          }
        })
      );
      setStatuses(out);
      setTs(new Date());
    };
    check();
    const i = setInterval(check, 30_000);
    return () => clearInterval(i);
  }, []);

  const allUp = Object.values(statuses).length === COMPONENTS.length && Object.values(statuses).every((s) => s.up);

  return (
    <div data-testid="status-page" className="container-pad py-12 md:py-20">
      <div className="overline">// STATUS</div>
      <h1 className="mt-4 font-display text-4xl md:text-5xl tracking-tight">System status</h1>

      <div className={`mt-8 card-flat p-6 flex items-center gap-4 ${allUp ? "border-signal/40" : "border-red-900"}`}>
        {allUp ? (
          <CheckCircle2 size={28} className="text-signal" />
        ) : (
          <AlertCircle size={28} className="text-red-400" />
        )}
        <div>
          <div className="font-display text-2xl tracking-tight">
            {allUp ? "All systems operational" : "Investigating degradation"}
          </div>
          <div className="mt-1 font-mono text-[11px] uppercase tracking-widest text-zinc-500">
            LAST CHECK · {ts ? ts.toLocaleString() : "CHECKING…"}
          </div>
        </div>
      </div>

      <div className="mt-8 card-flat divide-y divide-zinc-800">
        {COMPONENTS.map((c) => {
          const s = statuses[c.key];
          return (
            <div key={c.key} className="p-5 flex items-center justify-between" data-testid={`status-row-${c.key}`}>
              <div className="flex items-center gap-3">
                <Activity size={16} className={s?.up ? "text-signal" : "text-zinc-600"} />
                <div>
                  <div className="font-display text-base">{c.label}</div>
                  <div className="font-mono text-[11px] text-zinc-500 mt-0.5">{c.path}</div>
                </div>
              </div>
              <div className="text-right">
                <span className={`badge-mono ${s?.up ? "border-signal/60 text-signal" : "border-red-900 text-red-400"}`}>
                  {s ? (s.up ? "OPERATIONAL" : "DOWN") : "CHECKING"}
                </span>
                {s && (
                  <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                    {s.latency} ms · HTTP {s.code}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <p className="mt-8 text-zinc-500 text-sm max-w-2xl">
        Status auto-refreshes every 30 seconds. For incident history and post-mortems,
        email <a href="mailto:support@cloudrentvps.com" className="text-signal hover:underline">support@cloudrentvps.com</a>.
      </p>
    </div>
  );
}
